import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../../../services/api';
import { Button, Pagination } from '../../common';
import NodeEntityModal from './NodeEntityModal';
import './Nodes.css';

const PAGE_SIZE = 50;

const NodesTable = ({ 
  emptyMessage = 'No nodes available.', 
  searchPlaceholder = 'Search by node name or type...',
  onNodesChange,
  graphId,
  disableDrilldowns = false,
  // New props for Change Nodes/Edges page
  searchQuery: externalSearchQuery,
  searchMode = 'text',
  showOrphansOnly = false,
  showDeleted = false,
  selectedNodes,
  onSelectionChange,
  onDelete,
  onRestore,
  onNodeClick,
  enableSelection = false,
  enableDelete = false,
  requireExplicitSearch = false,
  nodeLinkSummary = null,
}) => {
  const location = useLocation();
  const [nodes, setNodes] = useState([]);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('name');

  // Request counter to prevent stale responses from overwriting newer data
  const requestIdRef = useRef(0);

  // Use external search query if provided, otherwise use internal
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;

  // Check if current page is expand-graph
  const isExpandGraphPage = location.pathname.includes('expand-graph');

  const fetchNodes = useCallback(async (query = '', pageNum = 1) => {
    if (!graphId) {
      setError('Graph ID is required to load nodes.');
      setNodes([]);
      return;
    }

    // Increment request counter and capture this request's id
    const currentRequestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      
      let response;
      // Use advanced search if mode or filters are specified
      if (searchMode === 'semantic' || showOrphansOnly || showDeleted) {
        response = await apiService.searchNodes({
          graphId,
          q: query,
          mode: searchMode,
          orphanOnly: showOrphansOnly,
          includeDeleted: showDeleted,
          page: pageNum,
          pageSize: PAGE_SIZE,
        });
      } else {
        response = await apiService.listNodes({ graphId, query, page: pageNum, pageSize: PAGE_SIZE });
      }

      // Only apply results if this is still the latest request
      if (currentRequestId !== requestIdRef.current) return;

      if (response.success) {
        setNodes(response.nodes || []);
        const total = response.totalCount || 0;
        setTotalCount(total);
        setTotalPages(Math.ceil(total / PAGE_SIZE) || 0);
      } else {
        setNodes([]);
        setTotalCount(0);
        setTotalPages(0);
        setError(response.message || 'Failed to load nodes.');
      }
    } catch (err) {
      // Ignore errors from stale requests
      if (currentRequestId !== requestIdRef.current) return;
      console.error('Failed to load nodes:', err);
      setNodes([]);
      setTotalCount(0);
      setTotalPages(0);
      setError(err.response?.data?.message || 'Failed to load nodes.');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [graphId, searchMode, showOrphansOnly, showDeleted]);

  // Reset page when search query changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, searchMode, showOrphansOnly, showDeleted]);

  useEffect(() => {
    if (requireExplicitSearch && externalSearchQuery === null) return;
    fetchNodes(searchQuery || '', page);
  }, [fetchNodes, searchQuery, page, requireExplicitSearch, externalSearchQuery]);

  // Notify parent of changes
  useEffect(() => {
    if (onNodesChange) {
      onNodesChange(nodes);
    }
  }, [nodes, onNodesChange]);

  // Handle node updates from navigation state
  useEffect(() => {
    if (location.state?.nodeUpdated) {
      fetchNodes(searchQuery);
    
      // Clear the state to prevent re-processing
      window.history.replaceState({ ...location.state, nodeUpdated: false }, '');
    }
  }, [location.state, fetchNodes, searchQuery]);

  // Determine empty state message
  const getEmptyMessage = () => {
    if (requireExplicitSearch && externalSearchQuery === null) {
      return 'Use the search bar above to find nodes.';
    }

    if (error) {
      return error;
    }

    if (loading) {
      return 'Loading nodes...';
    }

    if (searchQuery.trim() && nodes.length === 0) {
      return 'No nodes found matching your search.';
    }
    if (nodes.length === 0) {
      return emptyMessage;
    }
    return emptyMessage;
  };

  const openEntityModal = (node) => {
    setSelectedNode(node);
    setIsModalOpen(true);
  };

  const closeEntityModal = () => {
    setIsModalOpen(false);
    setSelectedNode(null);
  };

  const handleEntityUpdated = () => {
    fetchNodes(searchQuery);
  };

  const handleAddNewNode = () => {
    // Open modal in add mode (no node)
    setSelectedNode(null);
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="nodes-table-wrapper">
      {/* Header with Add Button */}
      {isExpandGraphPage && (
        <div className="nodes-header">
          <div className="nodes-header-right">
            <Button 
              variant="primary"
              onClick={handleAddNewNode}
            >
              + Add New Node
            </Button>
          </div>
        </div>
      )}
      
      {/* Search Box - only show if not using external search */}
      {externalSearchQuery === undefined && (
        <div className="nodes-search">
          <input
            type="text"
            className="nodes-search-input"
            placeholder={searchPlaceholder}
            value={internalSearchQuery}
            onChange={(e) => setInternalSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="table-loading-overlay">
          <div className="loading-spinner"></div>
          <p>Searching nodes...</p>
        </div>
      )}

      {/* Table or Empty State */}
      {nodes.length === 0 && !loading ? (
        <div className="nodes-table-container">
          <div className="empty-state">
            <p>{getEmptyMessage()}</p>
          </div>
        </div>
      ) : nodes.length > 0 ? (
        <div className="nodes-table-container">
          <div className="nodes-table-toolbar">
            <span className="nodes-table-count">{totalCount} nodes</span>
            <select
              className="nodes-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort: Name</option>
              <option value="type">Sort: Type</option>
              <option value="status">Sort: Status</option>
            </select>
          </div>
          <table className="nodes-table">
            <thead>
              <tr>
                {enableSelection && (
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={selectedNodes && selectedNodes.size === nodes.length && nodes.length > 0}
                      onChange={(e) => {
                        if (onSelectionChange) {
                          if (e.target.checked) {
                            onSelectionChange(new Set(nodes.map(n => n.id || n.nodeId)));
                          } else {
                            onSelectionChange(new Set());
                          }
                        }
                      }}
                    />
                  </th>
                )}
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Description</th>
                {enableDelete && <th className="actions-column">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {nodes.map((node) => {
                const nodeId = node.id || node.nodeId;
                const isDeleted = node.deletedAt || node.softDeletedAt;
                const isSelected = selectedNodes && selectedNodes.has(nodeId);
                
                return (
                  <tr 
                    key={nodeId} 
                    className={isDeleted ? 'deleted-row' : ''}
                  >
                    {enableSelection && (
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (onSelectionChange) {
                              const newSelection = new Set(selectedNodes);
                              if (e.target.checked) {
                                newSelection.add(nodeId);
                              } else {
                                newSelection.delete(nodeId);
                              }
                              onSelectionChange(newSelection);
                            }
                          }}
                        />
                      </td>
                    )}
                    <td className="node-cell">
                      {disableDrilldowns ? (
                        <span className="node-cell-text">{node.name || node.nodeName}</span>
                      ) : (
                        <button
                          type="button"
                          className="node-drilldown-link"
                          onClick={() => {
                            if (onNodeClick) {
                              onNodeClick(node);
                            } else {
                              openEntityModal(node);
                            }
                          }}
                          disabled={disableDrilldowns || !nodeId}
                        >
                          {node.name || node.nodeName}
                        </button>
                      )}
                      {nodeLinkSummary && nodeLinkSummary[nodeId] && (
                        <span
                          className="process-link-badge"
                          title={`${nodeLinkSummary[nodeId].link_count} process link(s)`}
                        >
                          🔗 {nodeLinkSummary[nodeId].link_count}
                        </span>
                      )}
                    </td>
                    <td className="node-cell">
                      <span className={`node-type-badge node-type-badge--${(node.type || node.nodeType || 'concept').replace(/\s+/g, '_')}`}>{node.type || node.nodeType || 'N/A'}</span>
                    </td>
                    <td className="node-cell">
                      <span className="node-cell-text">{node.status || 'active'}</span>
                    </td>
                    <td className="node-cell">
                      <span className="node-cell-text">{node.description || 'N/A'}</span>
                    </td>
                    {enableDelete && (
                      <td className="actions-column">
                        {isDeleted ? (
                          <button
                            type="button"
                            className="action-link restore-link"
                            onClick={() => onRestore && onRestore(nodeId)}
                          >
                            Restore
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="action-link delete-link"
                            onClick={() => onDelete && onDelete(nodeId, node.name || node.nodeName)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              showPageInfo={true}
              className="nodes-pagination"
            />
          )}
          {totalCount > 0 && (
            <div className="table-result-count">{totalCount} total results</div>
          )}
        </div>
      ) : null}
      </div>
      <NodeEntityModal
        isOpen={isModalOpen}
        node={selectedNode}
        onClose={closeEntityModal}
        onEntityUpdated={handleEntityUpdated}
        graphId={graphId}
      />
    </>
  );
};

export default NodesTable;

